import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { readLastOrg } from '@/features/auth/lastOrg'
import type { OrgOption } from '@/features/auth/types'
import { Building2, Loader2 } from 'lucide-react'

interface OrgSelectorProps {
  orgs: OrgOption[]
  /** Disables every choice while a selection is in flight. */
  busy?: boolean
  error?: string | null
  onSelect: (slug: string) => void
  /** Rendered under the list — "back to sign in", typically. */
  footer?: ReactNode
}

/**
 * The "which studio?" step. Shared by the password-login selector and the
 * Google OAuth org picker so both look and read the same, and so a locale fix
 * lands in one place.
 *
 * The last-used studio is sorted to the top, badged, and autofocused: a
 * non-technical user who only ever opens one of their two studios gets there
 * with a single Enter press, and everyone else still sees the whole list rather
 * than a hidden default.
 */
export function OrgSelector({ orgs, busy = false, error, onSelect, footer }: OrgSelectorProps) {
  const { t } = useTranslation()
  const lastOrg = readLastOrg()
  const ordered = [...orgs].sort((a, b) => {
    if (a.slug === lastOrg) return -1
    if (b.slug === lastOrg) return 1
    return 0
  })

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t('auth.orgSelector.title')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('auth.orgSelector.subtitle')}</p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3">
            {error && <p className="text-sm text-destructive text-center">{error}</p>}

            {ordered.map((org, index) => (
              <Button
                key={org.slug}
                type="button"
                variant="outline"
                autoFocus={index === 0}
                disabled={busy}
                onClick={() => onSelect(org.slug)}
                className="h-auto w-full justify-start gap-2 py-3"
              >
                {busy ? (
                  <Loader2 className="shrink-0 animate-spin" size={16} />
                ) : (
                  <Building2 className="shrink-0 text-muted-foreground" size={16} />
                )}
                <span className="truncate text-left">{org.name}</span>
                {org.slug === lastOrg && (
                  <span className="ml-auto shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t('auth.orgSelector.lastUsed')}
                  </span>
                )}
              </Button>
            ))}

            {footer}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
